import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import { useAuth } from './AuthContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  AddBusiness as AddSupplierIcon,
  Home as HomeIcon,
  LocalShipping as SupplierIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// NOTE: This mirrors EmployeeList.jsx's structure deliberately, for
// consistency with the rest of the app. Adjust the navigate() targets
// below to match your real SupplierCard route once confirmed.

const VAT_STATUS_LABELS = {
  unconfirmed: 'Unconfirmed',
  registered: 'Registered',
  not_registered: 'Not Registered'
};

const VAT_STATUS_COLORS = {
  unconfirmed: 'default',
  registered: 'success',
  not_registered: 'default'
};

const SupplierDirectory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [vatStatusFilter, setVatStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // GET /suppliers returns { success, data: [...], debug: {...} }
      // per the real supplier_routes.py — extended to include
      // vat_status, category and email per the backend patch.
      const response = await api.get('/suppliers');
      if (response.data?.success && Array.isArray(response.data.data)) {
        setSuppliers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Filter suppliers based on search and VAT status
  useEffect(() => {
    let filtered = [...suppliers];

    // VAT status filter
    if (vatStatusFilter !== 'all') {
      filtered = filtered.filter(sup => sup.vat_status === vatStatusFilter);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(sup =>
        sup.supplier_name?.toLowerCase().includes(search) ||
        sup.email?.toLowerCase().includes(search)
      );
    }

    setFilteredSuppliers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [suppliers, searchTerm, vatStatusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (supplier) => {
    // SupplierCard has no id-based lookup route (only /suppliers/<name>
    // via search), so this deep-links by name — SupplierCard.jsx needs
    // a small addition to read this on mount. See backend patch notes.
    navigate(`/supplier_card?search=${encodeURIComponent(supplier.supplier_name)}`);
  };

  const handleAddSupplier = () => {
    navigate('/supplier_card?mode=new');
  };

  const formatVatStatus = (vatStatus) => {
    return VAT_STATUS_LABELS[vatStatus] || 'Unconfirmed';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return '#9e9e9e';
    const colors = [
      '#1976D2', '#2E7D32', '#BF360C', '#6A1B9A',
      '#AD1457', '#00695C', '#D84315', '#283593'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // A number of bank-import-created suppliers currently store a
  // placeholder email (not-provided@salonlogicpro.local per handover).
  // Show that honestly rather than as a real contact address.
  const isPlaceholderEmail = (email) => {
    return !email || email.toLowerCase().includes('not-provided@');
  };

  // Paginated data
  const paginatedSuppliers = filteredSuppliers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const unconfirmedCount = suppliers.filter(s => s.vat_status === 'unconfirmed' || !s.vat_status).length;

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #1976D2, #14548f)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 0
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/bills')}
            sx={{
              color: 'white',
              mb: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Bills
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255,255,255,0.2)'
            }}>
              <SupplierIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                Supplier Directory
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}
                {unconfirmedCount > 0 && ` • ${unconfirmedCount} awaiting VAT confirmation`}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {/* Action Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: '1px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />

              {/* VAT Status Filter */}
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <Select
                  value={vatStatusFilter}
                  onChange={(e) => setVatStatusFilter(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Suppliers</MenuItem>
                  <MenuItem value="unconfirmed">Unconfirmed Only</MenuItem>
                  <MenuItem value="registered">Registered Only</MenuItem>
                  <MenuItem value="not_registered">Not Registered Only</MenuItem>
                </Select>
              </FormControl>

              {/* Refresh */}
              <Tooltip title="Refresh list">
                <IconButton onClick={fetchSuppliers} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Add Button */}
            <Button
              variant="contained"
              startIcon={<AddSupplierIcon />}
              onClick={handleAddSupplier}
              sx={{
                background: 'linear-gradient(135deg, #2E7D32, #256428)',
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Add Supplier
            </Button>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Supplier Table */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8
            }}>
              <CircularProgress />
            </Box>
          ) : filteredSuppliers.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary'
            }}>
              <SupplierIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {searchTerm || vatStatusFilter !== 'all'
                  ? 'No suppliers match your filters'
                  : 'No suppliers yet'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                {searchTerm || vatStatusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by adding your first supplier'}
              </Typography>
              {!searchTerm && vatStatusFilter === 'all' && (
                <Button
                  variant="contained"
                  startIcon={<AddSupplierIcon />}
                  onClick={handleAddSupplier}
                >
                  Add Your First Supplier
                </Button>
              )}
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                        Supplier
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '30%' }}>
                        Contact
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%' }}>
                        VAT Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSuppliers.map((supplier) => (
                      <TableRow
                        key={supplier.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f8ff' }
                        }}
                        onClick={() => handleRowClick(supplier)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: getAvatarColor(supplier.supplier_name),
                                fontWeight: 600
                              }}
                            >
                              {getInitials(supplier.supplier_name)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {supplier.supplier_name || 'Unnamed'}
                              </Typography>
                              {supplier.type && (
                                <Typography variant="caption" color="text.secondary">
                                  {supplier.type}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {supplier.email && !isPlaceholderEmail(supplier.email) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                  {supplier.email}
                                </Typography>
                              </Box>
                            )}
                            {isPlaceholderEmail(supplier.email) && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No email on file
                              </Typography>
                            )}
                            {supplier.contact_number && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {supplier.contact_number}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatVatStatus(supplier.vat_status)}
                            size="small"
                            color={VAT_STATUS_COLORS[supplier.vat_status] || 'default'}
                            sx={{
                              fontWeight: 500,
                              ...((!supplier.vat_status || supplier.vat_status === 'unconfirmed') && {
                                bgcolor: '#fff3e0',
                                color: '#BF360C'
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(supplier);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Supplier">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/supplier_card?search=${encodeURIComponent(supplier.supplier_name)}`);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={filteredSuppliers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ borderTop: '1px solid #e0e0e0' }}
              />
            </>
          )}
        </Paper>

        {/* Summary Stats */}
        {!loading && suppliers.length > 0 && (
          <Box sx={{
            mt: 3,
            display: 'flex',
            gap: 3,
            justifyContent: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              <strong>{suppliers.filter(s => s.vat_status === 'registered').length}</strong> registered
            </Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">
              <strong>{suppliers.filter(s => s.vat_status === 'not_registered').length}</strong> not registered
            </Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">
              <strong>{unconfirmedCount}</strong> unconfirmed
            </Typography>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">
              <strong>{suppliers.length}</strong> total
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SupplierDirectory;